import { API_BASE } from '../apiBase';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAddressSuggestions, geocodePlaceId } from '../lib/googlePlaces';
import { fmtPhoneInput } from '../lib/phone';
import CompanionEmpty from '../lib/CompanionEmpty';

function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Overwrite the saved memory when the user returns to the main list
  useEffect(() => {
    localStorage.setItem('lastProjectUrl', '/projects');
  }, []);

  // Create modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const modalRef = useRef(null); // Ref for click-outside logic
  const [newProject, setNewProject] = useState({
    poNumber: '',
    title: '',
    customer: '',
    status: 'Lead',
    billTo: 'Business', // only used when the chosen customer has a businessName
    startDate: '',
    endDate: '', // Added End Date
    value: '',
    projectManager: '',
    jobSiteAddress: {
      street: '',
      city: '',
      state: 'CA',
      zip: '',
      country: 'USA'
    },
    sameAsCustomerAddress: false
  });
  const [createError, setCreateError] = useState('');
// Loading UI states
  const [isCreating, setIsCreating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  // Customer dropdown search states
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const customerDropdownRef = useRef(null);
  // Inline "quick create customer" — add a person without leaving this modal
  const [quickCust, setQuickCust] = useState(null); // null | {firstName,lastName,phone,email,type}
  const [quickCustSaving, setQuickCustSaving] = useState(false);
  const [quickCustError, setQuickCustError] = useState('');

  // Address manual override state
  const [isManualAddress, setIsManualAddress] = useState(false);

  // Inline status editing
  const [editingStatusId, setEditingStatusId] = useState(null);

  // Inline title editing
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [tempTitle, setTempTitle] = useState("");

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedCell, setCopiedCell] = useState(null);
  const [users, setUsers] = useState([]); // for the Project Manager dropdown
  const currentRole = localStorage.getItem('role');
  const isSuperAdmin = currentRole === 'superadmin';
  const [sortMode, setSortMode] = useState('recent');  // 'recent' | 'po'
  const [recentOrder, setRecentOrder] = useState({});  // projectId -> recency index (0 = newest)

  // Address autocomplete states
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressError, setAddressError] = useState(null);

  // End date follows the start date until the user sets it by hand.
  // (Otherwise a backdated project forces paging the end-date picker
  // back from the current month every time.)
  const [endDateTouched, setEndDateTouched] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      try {
        setLoading(true);
        const projectsRes = await axios.get(`${API_BASE}/projects`, {
          headers: { Authorization: token }
        });
        setProjects(projectsRes.data);

        // Customers feed the Create Project modal — employees are (rightly)
        // refused by the API, so this must never sink the whole page.
        try {
          const customersRes = await axios.get(`${API_BASE}/customers`, {
            headers: { Authorization: token }
          });
          setCustomers(customersRes.data);
        } catch (_) { setCustomers([]); }

        // Users for the Project Manager dropdown (super admin / admin / PM roles).
        try {
          const usersRes = await axios.get(`${API_BASE}/users`, { headers: { Authorization: token } });
          const eligible = (usersRes.data || []).filter(u => ['superadmin', 'admin', 'projectManager'].includes(u.role));
          setUsers(eligible);
          const sa = eligible.find(u => u.role === 'superadmin');
          if (sa) setNewProject(prev => ({ ...prev, projectManager: prev.projectManager || sa._id }));
        } catch (_) { /* non-fatal */ }

        // My recently-viewed order (for the "Recently viewed" sort).
        try {
          const meRes = await axios.get(`${API_BASE}/me`, { headers: { Authorization: token } });
          const order = {};
          (meRes.data?.recentProjects || []).forEach((r, i) => {
            const pid = r.project?._id || r.project;
            if (pid) order[pid] = i;
          });
          setRecentOrder(order);
        } catch (_) { /* non-fatal */ }

        if (projectsRes.data.length > 0) {
          const maxPO = Math.max(...projectsRes.data.map(p => parseInt(p.poNumber || '0', 10)));
          setNewProject(prev => ({ ...prev, poNumber: String(maxPO + 1).padStart(4, '0') }));
        } else {
          setNewProject(prev => ({ ...prev, poNumber: '0001' }));
        }
      } catch (err) {
        console.error('Fetch error:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

   
  // ==================== IMPROVED DROPBOX FOLDER CREATION ====================
  const createProjectDropboxFolders = async (poNumber, street, city) => {
    const year = new Date().getFullYear();
    
    const cleanStreet = street 
      ? street.replace(/\s+/g, '').replace(/[^a-zA-Z0-9]/g, '') 
      : 'NoAddress';
    
    const cleanCity = city 
      ? city.replace(/[^a-zA-Z0-9]/g, '') 
      : '';

    const projectFolderName = `${poNumber}_${cleanStreet}_${cleanCity}`;
    const basePath = `/StalwartElectricProjects/${year}/Projects/${projectFolderName}`;

    console.log('Creating Dropbox structure for:', projectFolderName);

    try {
      // Level 1 - Root
      await axios.post(`${API_BASE}/dropbox/create-folder`, { path: '/StalwartElectricProjects' });
      
      // Level 2 - Year
      await axios.post(`${API_BASE}/dropbox/create-folder`, { path: `/StalwartElectricProjects/${year}` });
      
      // Level 3 - Projects
      await axios.post(`${API_BASE}/dropbox/create-folder`, { path: `/StalwartElectricProjects/${year}/Projects` });
      
      // Level 4 - Project folder
      await axios.post(`${API_BASE}/dropbox/create-folder`, { path: basePath });

      // Level 5 - Main subfolders
      await axios.post(`${API_BASE}/dropbox/create-folder`, { path: `${basePath}/ProjectFiles` });
      await axios.post(`${API_BASE}/dropbox/create-folder`, { path: `${basePath}/AccountsReceivable` });
      await axios.post(`${API_BASE}/dropbox/create-folder`, { path: `${basePath}/AccountsPayable` });

      // Level 6 - AccountsReceivable subfolders
      await axios.post(`${API_BASE}/dropbox/create-folder`, { path: `${basePath}/AccountsReceivable/Invoices` });
      await axios.post(`${API_BASE}/dropbox/create-folder`, { path: `${basePath}/AccountsReceivable/Quotes` });
      await axios.post(`${API_BASE}/dropbox/create-folder`, { path: `${basePath}/AccountsReceivable/ChangeOrders` });
      await axios.post(`${API_BASE}/dropbox/create-folder`, { path: `${basePath}/AccountsReceivable/Receipts` });

      // Level 7 - AccountsPayable/Receipts
      await axios.post(`${API_BASE}/dropbox/create-folder`, { path: `${basePath}/AccountsPayable/Receipts` });

      console.log('✅ SUCCESS: Full Dropbox structure created for', projectFolderName);
      return basePath;
    } catch (err) {
      console.error('❌ Dropbox creation error:', err.response?.data || err.message);
      // Still continue - don't break project creation
      return null;
    }
  };
  // =====================================================================

  // Handle click outside modal to cancel
  useEffect(() => {
    const handleClickOutside = (event) => {
      // If the clicked element was REMOVED by a re-render in the same instant
      // (e.g. the "+ New customer" row closes its own dropdown on mousedown),
      // it's detached from the document by the time we check — .contains()
      // would wrongly call that an "outside" click and close the whole modal
      // mid-flow. A detached target is never an outside click.
      if (!document.contains(event.target)) return;
      if (showCreateModal && modalRef.current && !modalRef.current.contains(event.target)) {
        handleCancelCreate();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCreateModal]);

  const handleCancelCreate = () => {
    setShowCreateModal(false);
    setAddressQuery('');
    setAddressSuggestions([]);
    setCustomerSearch('');
    setShowCustomerDropdown(false);
    setIsManualAddress(false);
  };

  const filteredProjects = useMemo(() => {
    if (!searchTerm.trim()) return projects;
    const lower = searchTerm.toLowerCase();
    return projects.filter(p =>
      p.poNumber?.includes(searchTerm) ||
      p.title?.toLowerCase().includes(lower) ||
      p.customer?.name?.toLowerCase().includes(lower) ||
      p.customer?.nickname?.toLowerCase().includes(lower) ||
      p.status?.toLowerCase().includes(lower) ||
      (p.tags && p.tags.some(tag => tag.toLowerCase().includes(lower))) ||
      // NEW: Tell the search bar to look at the address fields!
      p.jobSiteAddress?.street?.toLowerCase().includes(lower) ||
      p.jobSiteAddress?.city?.toLowerCase().includes(lower) ||
      p.jobSiteAddress?.zip?.toLowerCase().includes(lower)
    );
  }, [projects, searchTerm]);

  // Sort: 'recent' (by my view order) or 'po' (numeric PO).
  const displayedProjects = useMemo(() => {
    const arr = [...filteredProjects];
    if (sortMode === 'recent') {
      arr.sort((a, b) => {
        const ra = recentOrder[a._id] ?? Infinity;
        const rb = recentOrder[b._id] ?? Infinity;
        if (ra !== rb) return ra - rb;                       // most-recently-viewed first
        // Never-viewed projects: NEWEST PO first, so a just-created project
        // sits at the top of the unviewed pack instead of dead last.
        return parseInt(b.poNumber || '0', 10) - parseInt(a.poNumber || '0', 10);
      });
    } else {
      arr.sort((a, b) => parseInt(b.poNumber || '0', 10) - parseInt(a.poNumber || '0', 10)); // newest PO first
    }
    return arr;
  }, [filteredProjects, sortMode, recentOrder]);

  const filteredCustomers = useMemo(() => {
    return customers.filter(c =>
      `${c.name} ${c.nickname || ''} ${c.businessName || ''}`.toLowerCase().includes(customerSearch.toLowerCase())
    );
  }, [customers, customerSearch]);

  const statusColors = {
    'Prospective': 'bg-purple-600/80 text-white',
    'Captured': 'bg-blue-600/80 text-white',
    'Active': 'bg-cyan-600/80 text-white',
    'Completed': 'bg-green-600/80 text-white',
    'Lost': 'bg-red-600/80 text-white',
  };

  const copyToClipboard = async (text, rowId, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCell({ rowId, field });
      setTimeout(() => setCopiedCell(null), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleRowClick = (project) => {
    navigate(`/projects/${project._id}`);
  };

  const handleContextMenu = (e, project) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      projectId: project._id
    });
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete(`${API_BASE}/projects/${projectId}`, {
        headers: { Authorization: token }
      });
      if (res.status === 200 || res.data.success) {
        setProjects(prev => prev.filter(p => p._id !== projectId));
        setContextMenu(null);
      } else {
        alert('Delete failed');
      }
    } catch (err) {
      alert('Failed to delete project');
    }
  };

  // Close context menu
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

const fetchAddressSuggestions = debounce(async (query) => {
    if (query.length < 3 || isManualAddress) {
      setAddressSuggestions([]);
      setAddressError(null);
      setShowSuggestions(false);
      return;
    }
    setAddressLoading(true);
    const { predictions, error } = await getAddressSuggestions(query);
    setAddressLoading(false);
    setAddressError(error);
    setAddressSuggestions(predictions);
    setShowSuggestions(true);
  }, 400);

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    setNewProject(prev => ({ ...prev, sameAsCustomerAddress: checked }));
    if (checked && newProject.customer) {
      const selectedCustomer = customers.find(c => c._id === newProject.customer);
      if (selectedCustomer?.billingAddress) {
        setNewProject(prev => ({
          ...prev,
          jobSiteAddress: { ...selectedCustomer.billingAddress }
        }));
        setAddressQuery('');
        setAddressSuggestions([]);
      }
    } else if (!checked) {
      setNewProject(prev => ({
        ...prev,
        jobSiteAddress: { street: '', city: '', state: 'CA', zip: '', country: 'USA' }
      }));
    }
  };

  const handleSelectCustomer = (customer) => {
    setNewProject(prev => ({ ...prev, customer: customer._id }));
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
    if (newProject.sameAsCustomerAddress) {
      if (customer.billingAddress) {
        setNewProject(prev => ({
          ...prev,
          jobSiteAddress: { ...customer.billingAddress }
        }));
        setAddressQuery('');
        setAddressSuggestions([]);
      }
    }
  };

  // Create a customer inline (name + contact only) and select it for this project.
  const saveQuickCustomer = async () => {
    if (!quickCust?.firstName?.trim()) { setQuickCustError('First name is required'); return; }
    setQuickCustSaving(true); setQuickCustError('');
    try {
      const token = localStorage.getItem('token');
      const payload = {
        firstName: quickCust.firstName.trim(), lastName: (quickCust.lastName || '').trim(),
        name: `${quickCust.firstName} ${quickCust.lastName || ''}`.replace(/\s+/g, ' ').trim(),
        type: quickCust.type || 'Homeowner', status: 'Lead',
        primaryPhone: (quickCust.phone || '').trim(), primaryEmail: (quickCust.email || '').trim(),
        billingAddress: { street: '', city: '', state: 'CA', zip: '' },
      };
      const res = await axios.post(`${API_BASE}/customers`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setCustomers(prev => [...prev, res.data]);
      handleSelectCustomer(res.data);
      setQuickCust(null);
    } catch (err) {
      setQuickCustError(err.response?.data?.error || 'Failed to create customer');
    } finally {
      setQuickCustSaving(false);
    }
  };

  const handleStatusChange = async (projectId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/projects/${projectId}`, { status: newStatus }, {
        headers: { Authorization: token }
      });
      setProjects(prev =>
        prev.map(p => p._id === projectId ? { ...p, status: newStatus } : p)
      );
    } catch (err) {
      alert('Failed to update status');
    } finally {
      setEditingStatusId(null);
    }
  };

  // Click-to-pick status pill (same UX as the Contacts People tab) — replaces
  // the old inline <select>. Returns the pill + a colored-dot popover.
  const PROJECT_STATUSES = ['Lead', 'Bid Sent', 'Prospective', 'Captured', 'Active', 'Completed', 'Lost'];
  const renderStatusPill = (project) => {
    const isOpen = editingStatusId === project._id;
    return (
      <span className="relative inline-block" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setEditingStatusId(isOpen ? null : project._id); }}
          className={`px-3 py-1 text-xs font-medium rounded-full cursor-pointer hover:ring-2 hover:ring-white/20 transition-all ${statusColors[project.status] || 'bg-gray-600/80 text-white'}`}
          title="Click to change status"
        >
          {project.status}
        </button>
        {isOpen && (
          <>
            <span className="fixed inset-0 z-30" onClick={(e) => { e.stopPropagation(); setEditingStatusId(null); }} />
            <div className="absolute z-40 top-8 left-0 bg-gray-900 border border-gray-700 rounded-xl shadow-xl p-1 flex flex-col min-w-[9rem]">
              {PROJECT_STATUSES.map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setEditingStatusId(null); handleStatusChange(project._id, s); }}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-800 text-left"
                >
                  <span className={`w-2 h-2 rounded-full ${(statusColors[s] || 'bg-gray-500').split(' ')[0]}`} />
                  <span className={`text-[12px] ${s === project.status ? 'text-white font-semibold' : 'text-zinc-300'}`}>{s}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </span>
    );
  };

  const handleRenameTitle = async (projectId) => {
    const originalProject = projects.find(p => p._id === projectId);
    if (!tempTitle.trim() || tempTitle === originalProject.title) {
      setEditingTitleId(null);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/projects/${projectId}`, { title: tempTitle.trim() }, {
        headers: { Authorization: token }
      });
      setProjects(prev => prev.map(p => p._id === projectId ? { ...p, title: tempTitle.trim() } : p));
    } catch (err) {
      alert("Failed to rename project");
    } finally {
      setEditingTitleId(null);
    }
  };

  // ==================== ONLY SANITIZATION ADDED HERE ====================
   const handleCreateProject = async () => {
    if (!newProject.title.trim() || !newProject.customer) {
      setCreateError('Title and Customer are required');
      return;
    }

    const cleanValue = newProject.value 
      ? parseFloat(String(newProject.value).replace(/[$,]/g, '')) || 0 
      : 0;

    try {
      // 1. Turn on the loading spinner and clear old errors
      setIsCreating(true);
      setCreateError('');

      const token = localStorage.getItem('token');

      const payload = {
        ...newProject,
        value: cleanValue,
        startDate: newProject.startDate ? newProject.startDate : null,
        endDate: newProject.endDate ? newProject.endDate : null,
        projectManager: newProject.projectManager || undefined, // avoid empty-string cast error
      };

      const created = await axios.post(`${API_BASE}/projects`, payload, {
        headers: { Authorization: token }
      });

      const res = await axios.get(`${API_BASE}/projects`, {
        headers: { Authorization: token }
      });
      setProjects(res.data);

      // A project you just created IS your most recent — pin it to the very
      // top of the "Recently viewed" order immediately.
      const newId = created.data && created.data._id;
      if (newId) {
        setRecentOrder(prev => {
          const bumped = { [newId]: -1 };
          for (const [k, v] of Object.entries(prev)) bumped[k] = v + 1;
          return bumped;
        });
      }

      const maxPO = res.data.length > 0 
        ? Math.max(...res.data.map(p => parseInt(p.poNumber || '0', 10))) 
        : 0;

      setNewProject({
        poNumber: String(maxPO + 1).padStart(4, '0'),
        title: '',
        customer: '',
        status: 'Lead',
        billTo: 'Business',
        startDate: '',
        endDate: '',
        value: '',
        jobSiteAddress: { street: '', city: '', state: 'CA', zip: '', country: 'USA' },
        sameAsCustomerAddress: false
      });

      setAddressQuery('');
      setAddressSuggestions([]);
      setCustomerSearch('');
      setIsManualAddress(false);
      setEndDateTouched(false);

      // 2. Turn OFF loading, turn ON the Success Checkmark
      setIsCreating(false);
      setShowSuccess(true);

      // 3. Wait exactly 2.5 seconds, then close the modal
      setTimeout(() => {
        setShowSuccess(false);
        setShowCreateModal(false);
      }, 2500);

    } catch (err) {
      console.error(err);
      // If it fails, stop loading and show the error
      setIsCreating(false);
      setCreateError(err.response?.data?.error || 'Failed to create project');
    }
  };
  // ===================================================================

  if (loading) return <div className="min-h-screen bg-gray-950 text-zinc-100 p-8">Loading projects...</div>;
  if (error) return <div className="min-h-screen bg-gray-950 text-zinc-100 p-8 text-red-400">{error}</div>;

  // Employee with nothing assigned/visible yet → their companion keeps them company.
  if (currentRole === 'employee' && projects.length === 0) {
    return (
      <div className="min-h-screen bg-gray-950 text-zinc-100 p-8 max-md:p-4">
        <h1 className="text-3xl font-bold tracking-tight max-md:text-2xl">Projects</h1>
        <CompanionEmpty />
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-950 text-zinc-100 p-8 max-md:p-4 max-md:h-[calc(100dvh-3.5rem)] flex flex-col gap-6 max-md:gap-4 overflow-hidden">
      <div className="shrink-0 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 max-md:gap-3">
        <h1 className="text-3xl font-bold tracking-tight max-md:text-2xl">Projects</h1>
        {isSuperAdmin && (
          <button
            className="px-6 py-3 bg-indigo-700/90 hover:bg-indigo-600 text-white rounded-xl font-medium shadow-md transition-all hover:shadow-lg hover:scale-[1.02]"
            onClick={() => setShowCreateModal(true)}
          >
            + Create Project
          </button>
        )}
      </div>

      <div className="shrink-0 flex items-center gap-3 max-md:flex-col max-md:items-stretch">
        <input
          type="text"
          placeholder="Search by PO number, title, customer, status, or tags..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 max-w-xl max-md:max-w-none px-5 py-3.5 bg-gray-900 border border-gray-800 rounded-xl text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-600/60 focus:ring-2 focus:ring-indigo-600/20 transition-all max-md:text-base"
        />
        <div className="flex items-center bg-gray-900 border border-gray-800 rounded-xl p-1">
          <button
            onClick={() => setSortMode('recent')}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${sortMode === 'recent' ? 'bg-indigo-600 text-white' : 'text-zinc-400 hover:text-white'}`}
          >
            Recently viewed
          </button>
          <button
            onClick={() => setSortMode('po')}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${sortMode === 'po' ? 'bg-indigo-600 text-white' : 'text-zinc-400 hover:text-white'}`}
          >
            PO #
          </button>
        </div>
      </div>

      {/* ===== MOBILE CARD LIST (phones only — the desktop table below is untouched) ===== */}
      <div className="md:hidden flex-1 min-h-0 overflow-y-auto space-y-3 pb-4">
        {displayedProjects.length === 0 ? (
          <p className="text-center text-zinc-500 pt-16">No projects found. Try adjusting your search.</p>
        ) : (
          displayedProjects.map((project) => {
            const addr = [project.jobSiteAddress?.street, project.jobSiteAddress?.city].filter(Boolean).join(', ');
            return (
              <div
                key={project._id}
                onClick={() => handleRowClick(project)}
                className="bg-gray-900 border border-gray-800/60 rounded-2xl p-4 active:bg-gray-800/70 transition-colors"
              >
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="text-indigo-400 font-bold text-sm">#{project.poNumber}</span>
                  <span className="shrink-0">{renderStatusPill(project)}</span>
                </div>
                <p className="text-zinc-100 font-semibold leading-snug">{project.title}</p>
                <p className="text-zinc-400 text-sm mt-0.5">{project.customer?.name || 'N/A'}</p>
                {addr && <p className="text-zinc-500 text-xs mt-1.5">{addr}</p>}
                <p className="text-zinc-600 text-xs mt-1">
                  PM: {project.projectManager?.name || 'Super Admin'}
                  {(project.additionalManagers || []).filter(m => m.user?.name).map(m => ` · ${m.user.name}`).join('')}
                </p>
              </div>
            );
          })
        )}
      </div>

      <div className="max-md:hidden flex-1 min-h-0 flex flex-col bg-gray-900 rounded-2xl border border-gray-800/50 overflow-hidden shadow-2xl">
        <div className="flex-1 min-h-0 overflow-y-auto">
        <table className="min-w-full divide-y divide-gray-800/60">
          <thead className="bg-[#0d1321] sticky top-0 z-10">
            <tr>
              <th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">PO Number</th>
              <th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Title</th>
              <th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Customer</th>
              <th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Status</th>
              <th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Start Date</th>
              <th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Address</th>
              <th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Project Manager</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800/40">
            {displayedProjects.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-8 py-20 text-center text-zinc-500 text-lg">
                  No projects found. Try adjusting your search.
                </td>
              </tr>
            ) : (
              displayedProjects.map((project) => (
                <tr
                  key={project._id}
                  className="bg-gray-900 hover:bg-gray-800/50 hover:shadow-sm transition-all duration-200 cursor-pointer group"
                  onContextMenu={(e) => handleContextMenu(e, project)}
                  onClick={(e) => {
                    if (!e.target.closest('span') && !editingStatusId && !editingTitleId) {
                      handleRowClick(project);
                    }
                  }}
                >
                  <td className="px-8 py-6 whitespace-nowrap text-sm font-medium text-zinc-100">
                    <span
                      className="cursor-pointer relative group inline-block"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(project.poNumber, project._id, 'poNumber');
                      }}
                    >
                      {project.poNumber}
                      {copiedCell?.rowId === project._id && copiedCell?.field === 'poNumber' && (
                        <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10 animate-fade-in-out">
                          Copied!
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap text-sm text-zinc-300">
                    {editingTitleId === project._id ? (
                      <input
                        autoFocus
                        className="bg-gray-800 border border-indigo-500 rounded px-2 py-1 text-white outline-none w-full"
                        value={tempTitle}
                        onChange={(e) => setTempTitle(e.target.value)}
                        onBlur={() => handleRenameTitle(project._id)}
                        onKeyDown={(e) => e.key === 'Enter' && handleRenameTitle(project._id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <span
                        className="cursor-pointer relative group inline-block"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(project.title, project._id, 'title');
                        }}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          setEditingTitleId(project._id);
                          setTempTitle(project.title);
                        }}
                      >
                        {project.title}
                        {copiedCell?.rowId === project._id && copiedCell?.field === 'title' && (
                          <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10 animate-fade-in-out">
                            Copied!
                          </span>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap text-sm text-zinc-300">
                    <span
                      className="cursor-pointer relative group inline-block"
                      onClick={(e) => {
                        e.stopPropagation();
                        const customerName = project.customer?.name || 'N/A';
                        copyToClipboard(customerName, project._id, 'customer');
                      }}
                    >
                      {project.customer?.name || 'N/A'}
                      {copiedCell?.rowId === project._id && copiedCell?.field === 'customer' && (
                        <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10 animate-fade-in-out">
                          Copied!
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap">
                    {renderStatusPill(project)}
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap text-sm text-zinc-300">
                    <span
                      className="cursor-pointer relative group inline-block"
                      onClick={(e) => {
                        e.stopPropagation();
                        const dateStr = project.startDate
                          ? new Date(project.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                          : 'N/A';
                        copyToClipboard(dateStr, project._id, 'startDate');
                      }}
                    >
                      {project.startDate
                        ? new Date(project.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                        : 'N/A'}
                      {copiedCell?.rowId === project._id && copiedCell?.field === 'startDate' && (
                        <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10 animate-fade-in-out">
                          Copied!
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-8 py-6 text-sm text-zinc-300">
                    {project.jobSiteAddress && (project.jobSiteAddress.street || project.jobSiteAddress.city || project.jobSiteAddress.state || project.jobSiteAddress.zip)
                      ? <span
                          className="cursor-pointer relative group inline-block"
                          onClick={(e) => {
                            e.stopPropagation();
                            const addr = `${project.jobSiteAddress.street || ''}${project.jobSiteAddress.street && (project.jobSiteAddress.city || project.jobSiteAddress.state || project.jobSiteAddress.zip) ? ', ' : ''}${project.jobSiteAddress.city || ''}${project.jobSiteAddress.city && (project.jobSiteAddress.state || project.jobSiteAddress.zip) ? ', ' : ''}${project.jobSiteAddress.state || ''} ${project.jobSiteAddress.zip || ''}`.trim();
                            copyToClipboard(addr, project._id, 'address');
                          }}
                        >
                          {`${project.jobSiteAddress.street || ''}${project.jobSiteAddress.street && (project.jobSiteAddress.city || project.jobSiteAddress.state || project.jobSiteAddress.zip) ? ', ' : ''}${project.jobSiteAddress.city || ''}${project.jobSiteAddress.city && (project.jobSiteAddress.state || project.jobSiteAddress.zip) ? ', ' : ''}${project.jobSiteAddress.state || ''} ${project.jobSiteAddress.zip || ''}`.trim()}
                          {copiedCell?.rowId === project._id && copiedCell?.field === 'address' && (
                            <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10 animate-fade-in-out">
                              Copied!
                            </span>
                          )}
                        </span>
                      : 'N/A'}
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap text-sm text-zinc-300">
                    {project.projectManager?.name
                      ? <span className="inline-flex items-center gap-1.5">
                          {project.projectManager.role === 'superadmin' && <span className="text-[9px] uppercase tracking-wide text-indigo-400 font-bold">SA</span>}
                          {project.projectManager.name}
                        </span>
                      : <span className="text-zinc-600">Super Admin</span>}
                    {/* Additional (co-)project managers, one per line */}
                    {(project.additionalManagers || []).filter(m => m.user?.name).map(m => (
                      <div key={m.user._id} className="mt-1 text-xs text-zinc-500 flex items-center gap-1.5">
                        <span className="text-[9px] uppercase tracking-wide text-zinc-600 font-bold">+</span>
                        {m.user.name}
                      </div>
                    ))}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div ref={modalRef} className="bg-gray-900 rounded-2xl p-8 w-full max-w-lg border border-gray-700 max-h-[90vh] overflow-y-auto shadow-2xl">
            <h2 className="text-2xl font-bold mb-6">Create New Project</h2>
            {createError && <p className="text-red-400 mb-4">{createError}</p>}
            <div className="space-y-4">
              <div>
                <label className="block text-zinc-400 text-sm mb-1">PO Number</label>
                <input
                  value={newProject.poNumber}
                  onChange={(e) => setNewProject({ ...newProject, poNumber: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200 focus:outline-none focus:border-indigo-500 transition-colors"
                  placeholder="e.g. 0001"
                />
              </div>
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Title *</label>
                <input
                  value={newProject.title}
                  onChange={(e) => setNewProject({ ...newProject, title: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200 focus:outline-none focus:border-indigo-500 transition-colors"
                  required
                />
              </div>
              <div className="relative">
                <label className="block text-zinc-400 text-sm mb-1">Customer *</label>
                <input
                  type="text"
                  placeholder="Type to search customers..."
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                    setShowCustomerDropdown(true);
                  }}
                  onFocus={() => setShowCustomerDropdown(true)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200 focus:outline-none focus:border-indigo-500 transition-colors"
                />
                {showCustomerDropdown && !quickCust && (
                  <div
                    ref={customerDropdownRef}
                    className="absolute w-full bg-gray-800 border border-gray-700 rounded-xl mt-1 max-h-[300px] overflow-y-auto z-[60] shadow-xl"
                  >
                    {filteredCustomers.length === 0 ? (
                      <div className="px-4 py-3 text-zinc-500 text-sm">No customers found</div>
                    ) : (
                      filteredCustomers.map(c => (
                        <div
                          key={c._id}
                          className="px-4 py-3 hover:bg-gray-700 cursor-pointer text-zinc-200 transition-colors border-b border-gray-700/50 last:border-0"
                          onClick={() => handleSelectCustomer(c)}
                        >
                          {c.name} <span className="text-xs text-zinc-500 ml-2">({c.type})</span>
                        </div>
                      ))
                    )}
                    {/* quick-create a customer without leaving this modal */}
                    <div
                      className="px-4 py-3 sticky bottom-0 bg-gray-800 border-t border-gray-700 text-indigo-400 hover:text-indigo-300 hover:bg-gray-700/60 cursor-pointer text-sm font-semibold"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setShowCustomerDropdown(false);
                        setQuickCustError('');
                        // Seed the first/last name from whatever they were searching for
                        const parts = customerSearch.trim().split(/\s+/);
                        setQuickCust({ firstName: parts[0] || '', lastName: parts.slice(1).join(' '), phone: '', email: '', type: 'Homeowner' });
                      }}
                    >
                      + New customer{customerSearch.trim() ? ` “${customerSearch.trim()}”` : ''}
                    </div>
                  </div>
                )}
                {quickCust && (
                  <div className="mt-2 bg-gray-800/70 border border-indigo-600/40 rounded-xl p-4 space-y-2">
                    <p className="text-xs font-bold uppercase tracking-wider text-indigo-300">New customer</p>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={quickCust.firstName} onChange={e => setQuickCust({ ...quickCust, firstName: e.target.value })} placeholder="First name *" className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-zinc-200 focus:outline-none focus:border-indigo-500" />
                      <input value={quickCust.lastName} onChange={e => setQuickCust({ ...quickCust, lastName: e.target.value })} placeholder="Last name" className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-zinc-200 focus:outline-none focus:border-indigo-500" />
                      <input value={quickCust.phone} onChange={e => setQuickCust({ ...quickCust, phone: fmtPhoneInput(e.target.value) })} placeholder="Phone" className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-zinc-200 focus:outline-none focus:border-indigo-500" />
                      <input value={quickCust.email} onChange={e => setQuickCust({ ...quickCust, email: e.target.value })} placeholder="Email" className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-zinc-200 focus:outline-none focus:border-indigo-500" />
                    </div>
                    <select value={quickCust.type} onChange={e => setQuickCust({ ...quickCust, type: e.target.value })} className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-zinc-200 focus:outline-none focus:border-indigo-500">
                      <option value="Homeowner">Homeowner</option><option value="Contractor">Contractor</option><option value="Government">Government</option><option value="Commercial">Commercial</option>
                    </select>
                    {quickCustError && <p className="text-red-400 text-xs">{quickCustError}</p>}
                    <p className="text-[11px] text-zinc-500">Just the basics — address and details can be added later on their customer page.</p>
                    <div className="flex gap-2 pt-1">
                      <button type="button" onClick={() => setQuickCust(null)} className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 text-zinc-200 rounded-lg text-sm font-semibold">Cancel</button>
                      <button type="button" onClick={saveQuickCustomer} disabled={quickCustSaving} className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-bold disabled:opacity-60">{quickCustSaving ? 'Saving…' : 'Create & select'}</button>
                    </div>
                  </div>
                )}
              </div>
              {/* Bill To — only shown when the selected customer is tied to a business.
                  Controls whether the business name + "Attn:" appears on this
                  project's Quote/Invoice/Receipt, or just the individual's name. */}
              {(() => {
                const sel = customers.find(c => c._id === newProject.customer);
                if (!sel?.businessName) return null;
                return (
                  <div>
                    <label className="block text-zinc-400 text-sm mb-1">
                      Bill To <span className="text-zinc-600">({sel.businessName})</span>
                    </label>
                    <select
                      value={newProject.billTo}
                      onChange={(e) => setNewProject(prev => ({ ...prev, billTo: e.target.value }))}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200 focus:outline-none focus:border-indigo-500 transition-colors"
                    >
                      <option value="Business">Business — {sel.businessName} (Attn: {sel.name})</option>
                      <option value="Individual">Individual — {sel.name} only</option>
                    </select>
                  </div>
                );
              })()}
              <div>
                <label className="flex items-center gap-3 cursor-pointer text-sm text-zinc-300">
                  <input
                    type="checkbox"
                    checked={newProject.sameAsCustomerAddress}
                    onChange={handleCheckboxChange}
                    className="w-5 h-5 accent-indigo-600"
                  />
                  Same as customer billing address
                </label>
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-zinc-400 text-sm">Job Site Address</label>
                  <button
                    type="button"
                    onClick={() => setIsManualAddress(!isManualAddress)}
                    className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
                  >
                    {isManualAddress ? 'Use Auto Search' : 'Enter Manually'}
                  </button>
                </div>
                <div className="space-y-3 relative">
                  {!isManualAddress ? (
                    <div>
                      <input
                        type="text"
                        value={addressQuery}
                        onChange={(e) => {
                          const val = e.target.value;
                          setAddressQuery(val);
                          fetchAddressSuggestions(val);
                        }}
                        onFocus={() => setShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 300)}
                        placeholder="Start typing street, city, or zip..."
                        disabled={newProject.sameAsCustomerAddress}
                        className={`w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200 focus:outline-none focus:border-indigo-500 transition-colors ${newProject.sameAsCustomerAddress ? 'opacity-60 cursor-not-allowed' : ''}`}
                      />
                      {addressLoading && <div className="absolute right-3 top-3 text-zinc-500 text-sm">...</div>}
                      {showSuggestions && (
                        <div className="absolute w-full bg-gray-800 border border-gray-700 rounded-xl mt-1 max-h-60 overflow-auto z-50 shadow-xl">
                          {addressError ? (
                            <div className="px-4 py-3 text-red-400 text-sm">{addressError}</div>
                          ) : addressSuggestions.length === 0 ? (
                            <div className="px-4 py-3 text-zinc-500 text-sm">No matching addresses found</div>
                          ) : (
                            addressSuggestions.map((place, index) => (
                              <div
                                key={index}
                                className="px-4 py-3 hover:bg-gray-700 cursor-pointer text-zinc-200 transition-colors border-b border-gray-700/50 last:border-0"
                                onClick={async () => {
                                  // 1. Instantly show the clean Google text in the search bar
                                  setAddressQuery(place.description);
                                  setShowSuggestions(false);

                                  // 2. Break it into parts and save to jobSiteAddress
                                  try {
                                    const parts = await geocodePlaceId(place.placeId);
                                    setNewProject(prev => ({
                                      ...prev,
                                      jobSiteAddress: { ...parts, country: 'USA' }
                                    }));
                                  } catch (e) {
                                    console.error('🚨 Could not parse selected address:', e.message);
                                  }
                                }}
                              >
                                {place.description}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <input
                        type="text"
                        placeholder="Street"
                        value={newProject.jobSiteAddress.street}
                        onChange={(e) => setNewProject({ ...newProject, jobSiteAddress: { ...newProject.jobSiteAddress, street: e.target.value } })}
                        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-zinc-200"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          placeholder="City"
                          value={newProject.jobSiteAddress.city}
                          onChange={(e) => setNewProject({ ...newProject, jobSiteAddress: { ...newProject.jobSiteAddress, city: e.target.value } })}
                          className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-zinc-200"
                        />
                        <input
                          type="text"
                          placeholder="Zip"
                          value={newProject.jobSiteAddress.zip}
                          onChange={(e) => setNewProject({ ...newProject, jobSiteAddress: { ...newProject.jobSiteAddress, zip: e.target.value } })}
                          className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-zinc-200"
                        />
                      </div>
                    </div>
                  )}
                  {!isManualAddress && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-zinc-400 bg-gray-800/50 p-4 rounded-xl border border-gray-700/30">
                      <div><strong>Street:</strong> {newProject.jobSiteAddress.street || '—'}</div>
                      <div><strong>City:</strong> {newProject.jobSiteAddress.city || '—'}</div>
                      <div><strong>State:</strong> {newProject.jobSiteAddress.state || '—'}</div>
                      <div><strong>Zip:</strong> {newProject.jobSiteAddress.zip || '—'}</div>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Status</label>
                <select
                  value={newProject.status}
                  onChange={(e) => setNewProject({ ...newProject, status: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200 focus:outline-none focus:border-indigo-500"
                >
                  <option value="Lead">Lead</option>
                  <option value="Bid Sent">Bid Sent</option>
                  <option value="Prospective">Prospective</option>
                  <option value="Captured">Captured</option>
                  <option value="Active">Active</option>
                  <option value="Completed">Completed</option>
                  <option value="Lost">Lost</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">Start Date</label>
                  <input
                    type="date"
                    value={newProject.startDate}
                    onChange={(e) => {
                      const start = e.target.value;
                      setNewProject(prev => {
                        // End date follows the start date unless the user set it
                        // manually — and even then, never let it end up before start.
                        const follow = !endDateTouched || (prev.endDate && start > prev.endDate);
                        return { ...prev, startDate: start, endDate: follow ? start : prev.endDate };
                      });
                    }}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200 focus:outline-none focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">End Date</label>
                  <input
                    type="date"
                    value={newProject.endDate}
                    min={newProject.startDate || undefined}
                    onChange={(e) => {
                      const end = e.target.value;
                      // Manually set → stop following. Cleared → resume following.
                      setEndDateTouched(!!end);
                      setNewProject(prev => ({ ...prev, endDate: end }));
                    }}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200 focus:outline-none focus:border-indigo-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Value</label>
                <input
                  type="text"
                  value={newProject.value}
                  onChange={(e) => setNewProject({ ...newProject, value: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200 focus:outline-none focus:border-indigo-500"
                  placeholder="e.g. 6000 or $10,000"
                />
              </div>
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Project Manager</label>
                <select
                  value={newProject.projectManager || ''}
                  onChange={(e) => setNewProject({ ...newProject, projectManager: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200 focus:outline-none focus:border-indigo-500"
                >
                  {users.map(u => (
                    <option key={u._id} value={u._id}>
                      {u.name}{u.role === 'superadmin' ? ' (Super Admin)' : u.role === 'projectManager' ? ' (PM)' : u.role === 'admin' ? ' (Admin)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

           <div className="flex justify-end gap-4 mt-10 min-h-[50px] items-center w-full">
              {isCreating && !showSuccess && (
                <div className="flex items-center justify-end w-full gap-3 text-indigo-400 font-medium">
                  <svg className="animate-spin h-5 w-5 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating project file repository...
                </div>
              )}
              
              {showSuccess && (
                <div className="flex items-center justify-end w-full gap-2 text-emerald-400 font-bold">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  Project created successfully!
                </div>
              )}

              {!isCreating && !showSuccess && (
                <>
                  <button
                    onClick={handleCancelCreate}
                    className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-medium transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateProject}
                    className="px-6 py-3 bg-indigo-700 hover:bg-indigo-600 text-white rounded-xl font-medium shadow-md transition-all hover:shadow-lg"
                  >
                    Create Project
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {contextMenu && (
        <div
          className="fixed bg-gray-900 border border-gray-700 rounded-lg shadow-2xl py-2 z-50"
          style={{ top: `${contextMenu.y}px`, left: `${contextMenu.x}px` }}
        >
          <button
            className="w-full px-6 py-2 text-left text-red-400 hover:bg-gray-800 hover:text-red-300 transition-colors"
            onClick={() => handleDelete(contextMenu.projectId)}
          >
            Delete Project
          </button>
        </div>
      )}

      <style>{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateY(8px); }
          20% { opacity: 1; transform: translateY(0); }
          80% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-8px); }
        }
        .animate-fade-in-out {
          animation: fadeInOut 3s ease-in-out forwards;
        }
        /* Custom scrollbar for the dropdowns */
        .max-h-\\[300px\\]::-webkit-scrollbar {
          width: 6px;
        }
        .max-h-\\[300px\\]::-webkit-scrollbar-track {
          background: #111827;
        }
        .max-h-\\[300px\\]::-webkit-scrollbar-thumb {
          background: #374151;
          border-radius: 10px;
        }
        .max-h-\\[300px\\]::-webkit-scrollbar-thumb:hover {
          background: #4B5563;
        }
      `}</style>
    </div>
  );
}

export default Projects;